import { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductCategory } from '@/mocks/products';

interface ProductCarouselProps {
  categories: ProductCategory[];
}

export default function ProductCarousel({ categories }: ProductCarouselProps) {
  const { t } = useTranslation();
  const scrollRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [canScrollLeft, setCanScrollLeft] = useState<Record<string, boolean>>({});
  const [canScrollRight, setCanScrollRight] = useState<Record<string, boolean>>({});

  const checkScroll = useCallback((categoryId: string) => {
    const el = scrollRefs.current[categoryId];
    if (!el) return;
    setCanScrollLeft((prev) => ({ ...prev, [categoryId]: el.scrollLeft > 4 }));
    setCanScrollRight((prev) => ({
      ...prev,
      [categoryId]: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    }));
  }, []);

  const scroll = useCallback(
    (categoryId: string, direction: 'left' | 'right') => {
      const el = scrollRefs.current[categoryId];
      if (!el) return;
      const cardWidth = 280 + 16; // card width + gap
      const scrollAmount = direction === 'left' ? -cardWidth * 2 : cardWidth * 2;
      el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(() => checkScroll(categoryId), 350);
    },
    [checkScroll]
  );

  const truncateTitle = (title: string) => {
    if (title.length <= 48) return title;
    return title.slice(0, 45) + '...';
  };

  return (
    <section className="w-full py-10 md:py-14 bg-background-50">
      {categories.map((cat) => (
        <div key={cat.id} className="mb-12 md:mb-16 last:mb-0">
          {/* Section header */}
          <div className="w-full px-4 md:px-6 lg:px-8 mb-6 md:mb-8 flex items-center justify-between">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight">
              {t(cat.nameKey)}
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => scroll(cat.id, 'left')}
                disabled={!canScrollLeft[cat.id]}
                className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-background-200 flex items-center justify-center transition-all hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Scroll left"
              >
                <i className="ri-arrow-left-s-line text-lg md:text-xl text-foreground-700" />
              </button>
              <button
                onClick={() => scroll(cat.id, 'right')}
                disabled={!canScrollRight[cat.id]}
                className="w-8 h-8 md:w-10 md:h-10 rounded-full border border-background-200 flex items-center justify-center transition-all hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Scroll right"
              >
                <i className="ri-arrow-right-s-line text-lg md:text-xl text-foreground-700" />
              </button>
            </div>
          </div>

          {/* Carousel track */}
          <div
            ref={(el) => {
              scrollRefs.current[cat.id] = el;
              if (el) {
                setTimeout(() => checkScroll(cat.id), 100);
              }
            }}
            onScroll={() => checkScroll(cat.id)}
            className="w-full overflow-x-auto scrollbar-hide px-4 md:px-6 lg:px-8"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex gap-4 md:gap-5 pb-2">
              {cat.products.map((product) => (
                <a
                  key={product.id}
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="group flex-shrink-0 w-[220px] sm:w-[250px] md:w-[280px] bg-white rounded-xl border border-background-200/60 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:border-background-300"
                >
                  <div className="w-full h-[220px] sm:h-[250px] md:h-[280px] overflow-hidden bg-background-100">
                    <img
                      src={product.image}
                      alt={product.title}
                      loading="lazy"
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3 md:p-4">
                    <h3
                      className="text-sm md:text-[15px] font-medium text-foreground-950 leading-snug mb-2 line-clamp-2"
                      title={product.title}
                    >
                      {truncateTitle(product.title)}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-primary-600">
                        {product.price}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-md bg-primary-50 text-primary-700 font-medium whitespace-nowrap">
                        {t('cta_buy_it_now')}
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}